/**
 * rtIsocenterCore.mjs — изоцентр мишени (центр масс структуры) и координаты.
 * Без браузера, проверяемо тестами.
 *
 *  - polygonAreaCentroid2D: площадь и центроид полигона (формула шнурков);
 *  - structureCentroidWorld: объёмный центр масс стопки контуров в мире (LPS);
 *  - anatomical: мировая точка → анатомические оси (R/L, A/P, S/I) от опоры.
 */

function _dot(a, b) { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; }
function _cross(a, b) { return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]; }
function _norm(v) { const n = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / n, v[1] / n, v[2] / n]; }

export function newellNormal(points) {
  let nx = 0, ny = 0, nz = 0;
  for (let i = 0; i < points.length; i++) {
    const c = points[i], n = points[(i + 1) % points.length];
    nx += (c[1] - n[1]) * (c[2] + n[2]);
    ny += (c[2] - n[2]) * (c[0] + n[0]);
    nz += (c[0] - n[0]) * (c[1] + n[1]);
  }
  return _norm([nx, ny, nz]);
}

/** Площадь и центроид 2D-полигона (массив [x,y]). Площадь со знаком → |area|. */
export function polygonAreaCentroid2D(poly) {
  let a2 = 0, cx = 0, cy = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const cr = poly[j][0] * poly[i][1] - poly[i][0] * poly[j][1];
    a2 += cr; cx += (poly[j][0] + poly[i][0]) * cr; cy += (poly[j][1] + poly[i][1]) * cr;
  }
  const area = a2 / 2;
  if (Math.abs(area) < 1e-9) {
    // вырожденный полигон — среднее вершин
    let mx = 0, my = 0; for (const p of poly) { mx += p[0]; my += p[1]; }
    return { area: 0, cx: mx / poly.length, cy: my / poly.length };
  }
  return { area: Math.abs(area), cx: cx / (6 * area), cy: cy / (6 * area) };
}

/**
 * Объёмный центр масс структуры (мир, LPS) по стопке контуров.
 * @param {Array<{points:number[][]}>} contours
 * @returns {{center:number[], normal:number[], volumeProxy:number}}
 */
export function structureCentroidWorld(contours) {
  const list = (contours || []).filter(c => c && Array.isArray(c.points) && c.points.length >= 3);
  if (!list.length) return { center: [0, 0, 0], normal: [0, 0, 1], volumeProxy: 0, used: 0, skipped: 0 };

  // [7.0.12] Нормаль стопки НЕЛЬЗЯ считать простой суммой нормалей контуров —
  // ровно та же ловушка, что уже исправлена в rtDvhCore. Здесь она бьёт дважды:
  //
  //  1) контуры в разных проекциях дают сумму трёх перпендикуляров = диагональ;
  //  2) а главное — НАПРАВЛЕНИЕ ОБХОДА. Врач обводит опухоль от руки то по часовой
  //     стрелке, то против; у противоположно обойдённых контуров нормали
  //     противоположны и при сложении гасят друг друга. Замерено: если каждый
  //     второй контур обведён в обратную сторону, изоцентр уезжает на 11.8 мм.
  //     Для точки наведения это недопустимо.
  //
  // Поэтому: кластеризуем по |cos| (знак обхода игнорируем), берём главную
  // плоскость, а внутри неё складываем нормали с выравниванием знака.
  const norms = list.map(c => newellNormal(c.points));
  const clusters = [];
  for (let i = 0; i < list.length; i++) {
    let put = null;
    for (const cl of clusters) if (Math.abs(_dot(norms[i], cl.n)) > 0.985) { put = cl; break; }
    if (put) put.idx.push(i); else clusters.push({ n: norms[i], idx: [i] });
  }
  clusters.sort((a, b) => b.idx.length - a.idx.length);
  const main = clusters[0];
  const skipped = list.length - main.idx.length;

  let n = [0, 0, 0];
  for (const i of main.idx) {
    const s = _dot(norms[i], main.n) >= 0 ? 1 : -1;      // выравниваем направление обхода
    for (let q = 0; q < 3; q++) n[q] += s * norms[i][q];
  }
  n = _norm(n);
  const seed = Math.abs(n[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
  const u = _norm(_cross(n, seed));
  const v = _cross(n, u);

  let sumA = 0, acc = [0, 0, 0];
  for (const i of main.idx) {
    const pts = list[i].points;
    const poly = pts.map(p => [_dot(p, u), _dot(p, v)]);
    const { area, cx, cy } = polygonAreaCentroid2D(poly);
    // Плоскость контура — по ЦЕНТРОИДУ, а не по первой точке: первая точка несёт
    // всю неплоскостность обводки, центроид её усредняет.
    let sx = 0, sy = 0, sz = 0;
    for (const p of pts) { sx += p[0]; sy += p[1]; sz += p[2]; }
    const level = _dot([sx / pts.length, sy / pts.length, sz / pts.length], n);
    const wc = [
      cx * u[0] + cy * v[0] + level * n[0],
      cx * u[1] + cy * v[1] + level * n[1],
      cx * u[2] + cy * v[2] + level * n[2],
    ];
    const wgt = area || 1e-6;
    acc[0] += wgt * wc[0]; acc[1] += wgt * wc[1]; acc[2] += wgt * wc[2];
    sumA += wgt;
  }
  return {
    center: [acc[0] / sumA, acc[1] / sumA, acc[2] / sumA],
    normal: n, volumeProxy: sumA, used: main.idx.length, skipped,
  };
}

/**
 * Мировая точка (DICOM LPS) → анатомические координаты от опоры ref.
 * LPS: +x = влево (L), +y = кзади (P), +z = вверх (S).
 */
export function anatomical(w, ref = [0, 0, 0]) {
  const d = [w[0] - ref[0], w[1] - ref[1], w[2] - ref[2]];
  return {
    lps: d.slice(),
    x: { label: d[0] >= 0 ? 'L' : 'R', mm: Math.abs(d[0]) },
    y: { label: d[1] >= 0 ? 'P' : 'A', mm: Math.abs(d[1]) },
    z: { label: d[2] >= 0 ? 'S' : 'I', mm: Math.abs(d[2]) },
    dist: Math.hypot(d[0], d[1], d[2]),
  };
}

/** Человекочитаемая строка анатомических координат. */
export function formatAnatomical(a) {
  return `${a.x.label} ${a.x.mm.toFixed(1)} · ${a.y.label} ${a.y.mm.toFixed(1)} · ${a.z.label} ${a.z.mm.toFixed(1)} мм`;
}
